/**
 * AdminPostPermissionsPage
 *
 * Super admin page to manage post permissions for users
 * Allows granting/revoking custom permissions on a per-user basis
 */

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '@/lib/firebase';
import { useAuth } from '@/contexts/AuthContext';
import { User, UserRole } from '@/types';
import { Shield, Check, X, Search } from 'lucide-react';
import { toast } from 'sonner';

interface UserWithPermissions extends User {
  customPermissions?: {
    canCreate: boolean;
    canEdit: boolean;
    canDelete: boolean;
    canEditAny: boolean;
    canDeleteAny: boolean;
  };
}

interface Franchise {
  id: string;
  name: string;
}

interface Branch {
  id: string;
  name: string;
  franchiseId: string;
}

export default function AdminPostPermissionsPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [users, setUsers] = useState<UserWithPermissions[]>([]);
  const [filteredUsers, setFilteredUsers] = useState<UserWithPermissions[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [savingFor, setSavingFor] = useState<string | null>(null);
  const [activeFranchiseId, setActiveFranchiseId] = useState<string | null>(null);
  const [activeBranchId, setActiveBranchId] = useState<string | null>(null);
  const [franchises, setFranchises] = useState<Franchise[]>([]);
  const [branches, setBranches] = useState<Branch[]>([]);
  const [loadingFranchises, setLoadingFranchises] = useState(true);
  const [loadingBranches, setLoadingBranches] = useState(false);

  // Only super_admin can access
  if (!user || user.role !== 'super_admin') {
    return (
      <div className="min-h-screen bg-surface-2 flex items-center justify-center p-4">
        <div className="bg-surface rounded-lg shadow-sm p-8 text-center max-w-md">
          <p className="text-xl font-bold text-ink mb-4">
            🚫 Acceso Denegado
          </p>
          <p className="text-ink-muted mb-6">
            Solo el super administrador puede gestionar permisos de posts
          </p>
          <button
            onClick={() => navigate('/')}
            className="bg-info text-white px-6 py-2 rounded-lg hover:bg-info/90 transition"
          >
            Ir al Inicio
          </button>
        </div>
      </div>
    );
  }

  // Load franchises first
  useEffect(() => {
    const loadFranchises = async () => {
      try {
        setLoadingFranchises(true);
        const franchisesRef = collection(db, 'franchises');
        const snapshot = await getDocs(franchisesRef);

        const franchiseList = snapshot.docs.map(doc => ({
          id: doc.id,
          name: doc.data().name || doc.id,
        }));

        // Sort by name
        franchiseList.sort((a, b) => a.name.localeCompare(b.name));
        setFranchises(franchiseList);

        // Set default franchise (prefer Monkey Barbershop or first one)
        const defaultFranchise = franchiseList.find(f => f.name.includes('Monkey Barbershop')) || franchiseList[0];
        if (defaultFranchise && !activeFranchiseId) {
          setActiveFranchiseId(defaultFranchise.id);
        }
      } catch (error) {
        console.error('Error loading franchises:', error);
        toast.error('Error al cargar franquicias');
      } finally {
        setLoadingFranchises(false);
      }
    };

    loadFranchises();
  }, []);

  // Load branches when franchise changes
  useEffect(() => {
    const loadBranches = async () => {
      if (!activeFranchiseId) {
        setBranches([]);
        return;
      }

      try {
        setLoadingBranches(true);
        const branchesRef = collection(db, 'branches');
        const q = query(branchesRef, where('franchiseId', '==', activeFranchiseId));
        const snapshot = await getDocs(q);

        const branchList = snapshot.docs.map(doc => ({
          id: doc.id,
          name: doc.data().name || doc.id,
          franchiseId: doc.data().franchiseId,
        }));

        branchList.sort((a, b) => a.name.localeCompare(b.name));
        setBranches(branchList);
        setActiveBranchId(null); // Reset branch selection
      } catch (error) {
        console.error('Error loading branches:', error);
      } finally {
        setLoadingBranches(false);
      }
    };

    loadBranches();
  }, [activeFranchiseId]);

  // Load users when franchise or branch changes
  useEffect(() => {
    if (activeFranchiseId) {
      loadUsersForFranchise(activeFranchiseId, activeBranchId);
    }
  }, [activeFranchiseId, activeBranchId]);

  useEffect(() => {
    if (searchTerm.trim() === '') {
      setFilteredUsers(users);
    } else {
      const term = searchTerm.toLowerCase();
      setFilteredUsers(
        users.filter(
          (u) =>
            u.displayName.toLowerCase().includes(term) ||
            u.email?.toLowerCase().includes(term) ||
            u.role.toLowerCase().includes(term)
        )
      );
    }
  }, [searchTerm, users]);

  const loadUsersForFranchise = async (franchiseId: string, branchId: string | null = null) => {
    try {
      setLoading(true);

      // Get users - filter by branch if specified
      const usersRef = collection(db, 'users');
      let q;
      if (branchId) {
        q = query(
          usersRef,
          where('franchiseId', '==', franchiseId),
          where('branchId', '==', branchId)
        );
      } else {
        q = query(usersRef, where('franchiseId', '==', franchiseId));
      }
      const snapshot = await getDocs(q);

      const usersData = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      })) as UserWithPermissions[];

      // Get custom permissions for all users
      const getUserPerms = httpsCallable(functions, 'listFranchisePostPermissions');
      const permsResult = await getUserPerms({ franchiseId });
      const customPerms = (permsResult.data as any).permissions || [];

      // Merge custom permissions with users
      usersData.forEach((u) => {
        const userPerm = customPerms.find((p: any) => p.userId === u.id);
        if (userPerm) {
          u.customPermissions = {
            canCreate: userPerm.canCreate,
            canEdit: userPerm.canEdit,
            canDelete: userPerm.canDelete,
            canEditAny: userPerm.canEditAny,
            canDeleteAny: userPerm.canDeleteAny,
          };
        }
      });

      setUsers(usersData);
      setFilteredUsers(usersData);
    } catch (error) {
      console.error('Error loading users:', error);
      toast.error('Error al cargar usuarios');
    } finally {
      setLoading(false);
    }
  };

  const handleTogglePermission = async (
    userId: string,
    permission: keyof NonNullable<UserWithPermissions['customPermissions']>,
    currentValue: boolean
  ) => {
    if (!activeFranchiseId) return;

    try {
      setSavingFor(userId);

      // Get current custom permissions or create new object
      const targetUser = users.find((u) => u.id === userId);
      const currentPerms = targetUser?.customPermissions || {
        canCreate: false,
        canEdit: false,
        canDelete: false,
        canEditAny: false,
        canDeleteAny: false,
      };

      // Toggle the specific permission
      const newPerms = {
        ...currentPerms,
        [permission]: !currentValue,
      };

      // Call Cloud Function to grant permission
      const grantPerm = httpsCallable(functions, 'grantPostPermission');
      await grantPerm({
        userId,
        franchiseId: activeFranchiseId,
        permissions: newPerms,
      });

      // Update local state
      setUsers((prev) =>
        prev.map((u) =>
          u.id === userId
            ? { ...u, customPermissions: newPerms }
            : u
        )
      );

      toast.success('Permiso actualizado');
    } catch (error) {
      console.error('Error updating permission:', error);
      toast.error('Error al actualizar permiso');
    } finally {
      setSavingFor(null);
    }
  };

  const handleRevokeAll = async (userId: string) => {
    if (!activeFranchiseId) return;
    if (!confirm('¿Revocar todos los permisos personalizados de este usuario?')) return;

    try {
      setSavingFor(userId);

      const revokePerm = httpsCallable(functions, 'revokePostPermission');
      await revokePerm({
        userId,
        franchiseId: activeFranchiseId,
      });

      // Update local state
      setUsers((prev) =>
        prev.map((u) =>
          u.id === userId ? { ...u, customPermissions: undefined } : u
        )
      );

      toast.success('Permisos revocados. Usuario usa permisos por defecto.');
    } catch (error) {
      console.error('Error revoking permissions:', error);
      toast.error('Error al revocar permisos');
    } finally {
      setSavingFor(null);
    }
  };

  const getRoleBadgeColor = (role: UserRole) => {
    switch (role) {
      case 'super_admin':
        return 'bg-accent/10 text-accent bg-accent/10 text-accent';
      case 'franchise_owner':
        return 'bg-info/10 text-info bg-info/10 text-info';
      case 'admin':
        return 'bg-success/10 text-success bg-success/10 text-success';
      case 'barber':
        return 'bg-accent/10 text-accent bg-accent/10 text-accent';
      case 'client':
        return 'bg-surface-2 text-ink bg-surface-2 text-ink-muted';
      default:
        return 'bg-surface-2 text-ink bg-surface-2 text-ink-muted';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-surface-2 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-info/30 mx-auto mb-4"></div>
          <p className="text-ink-muted">Cargando usuarios...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-surface-2 py-8">
      <div className="max-w-7xl mx-auto px-4">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <Shield className="w-8 h-8 text-info" />
            <h1 className="text-3xl font-bold text-ink">
              Gestión de Permisos de Posts
            </h1>
          </div>
          <p className="text-ink-muted">
            Otorga o revoca permisos personalizados para crear, editar y borrar posts.
            Los usuarios sin permisos personalizados usan los permisos por defecto de su rol.
          </p>
        </div>

        {/* Franchise + Branch Selector + Search */}
        <div className="mb-6 flex flex-col sm:flex-row gap-4">
          {/* Franchise Selector */}
          <div className="sm:w-56">
            <label className="block text-sm font-medium text-ink-muted mb-1">
              Franquicia
            </label>
            <select
              value={activeFranchiseId || ''}
              onChange={(e) => setActiveFranchiseId(e.target.value)}
              disabled={loadingFranchises}
              className="w-full px-3 py-2 border border-hairline bg-surface text-ink rounded-lg focus:ring-2 focus:ring-focus focus:border-transparent"
            >
              {loadingFranchises ? (
                <option>Cargando...</option>
              ) : (
                franchises.map((f) => (
                  <option key={f.id} value={f.id}>
                    {f.name}
                  </option>
                ))
              )}
            </select>
          </div>

          {/* Branch Selector */}
          <div className="sm:w-56">
            <label className="block text-sm font-medium text-ink-muted mb-1">
              Sucursal
            </label>
            <select
              value={activeBranchId || ''}
              onChange={(e) => setActiveBranchId(e.target.value || null)}
              disabled={loadingBranches || branches.length === 0}
              className="w-full px-3 py-2 border border-hairline bg-surface text-ink rounded-lg focus:ring-2 focus:ring-focus focus:border-transparent"
            >
              <option value="">Todas las sucursales</option>
              {loadingBranches ? (
                <option>Cargando...</option>
              ) : (
                branches.map((b) => (
                  <option key={b.id} value={b.id}>
                    {b.name}
                  </option>
                ))
              )}
            </select>
          </div>

          {/* Search */}
          <div className="flex-1">
            <label className="block text-sm font-medium text-ink-muted mb-1">
              Buscar usuario
            </label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-ink-subtle" />
              <input
                type="text"
                placeholder="Buscar por nombre, email o rol..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-hairline bg-surface text-ink rounded-lg focus:ring-2 focus:ring-focus focus:border-transparent"
              />
            </div>
          </div>
        </div>

        {/* Users Table */}
        <div className="bg-surface rounded-lg shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-surface-2 border-b border-hairline">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-ink-subtle uppercase tracking-wider">
                    Usuario
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-ink-subtle uppercase tracking-wider">
                    Rol
                  </th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-ink-subtle uppercase tracking-wider">
                    Crear
                  </th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-ink-subtle uppercase tracking-wider">
                    Editar
                  </th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-ink-subtle uppercase tracking-wider">
                    Borrar
                  </th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-ink-subtle uppercase tracking-wider">
                    Editar Cualquiera
                  </th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-ink-subtle uppercase tracking-wider">
                    Borrar Cualquiera
                  </th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-ink-subtle uppercase tracking-wider">
                    Acciones
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-hairline">
                {filteredUsers.map((u) => (
                  <tr key={u.id} className="hover:bg-surface-2 transition">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-3">
                        {u.photoURL ? (
                          <img
                            src={u.photoURL}
                            alt={u.displayName}
                            className="w-10 h-10 rounded-full object-cover"
                          />
                        ) : (
                          <div className="w-10 h-10 rounded-full bg-info/10 flex items-center justify-center text-info font-bold">
                            {u.displayName.charAt(0).toUpperCase()}
                          </div>
                        )}
                        <div>
                          <p className="font-medium text-ink">{u.displayName}</p>
                          <p className="text-sm text-ink-subtle">{u.email}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span
                        className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getRoleBadgeColor(
                          u.role
                        )}`}
                      >
                        {u.role}
                      </span>
                    </td>
                    {(['canCreate', 'canEdit', 'canDelete', 'canEditAny', 'canDeleteAny'] as const).map((perm) => (
                      <td key={perm} className="px-6 py-4 whitespace-nowrap text-center">
                        <button
                          onClick={() =>
                            handleTogglePermission(
                              u.id,
                              perm,
                              u.customPermissions?.[perm] || false
                            )
                          }
                          disabled={savingFor === u.id}
                          className={`p-2 rounded-lg transition ${
                            u.customPermissions?.[perm]
                              ? 'bg-success/10 text-success bg-success/10 text-success'
                              : 'bg-danger/10 text-danger bg-danger/10 text-danger'
                          } ${
                            savingFor === u.id ? 'opacity-50 cursor-not-allowed' : 'hover:opacity-80'
                          }`}
                        >
                          {u.customPermissions?.[perm] ? (
                            <Check className="w-5 h-5" />
                          ) : (
                            <X className="w-5 h-5" />
                          )}
                        </button>
                      </td>
                    ))}
                    <td className="px-6 py-4 whitespace-nowrap text-center">
                      {u.customPermissions && (
                        <button
                          onClick={() => handleRevokeAll(u.id)}
                          disabled={savingFor === u.id}
                          className="text-sm text-danger hover:underline disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          Revocar Todo
                        </button>
                      )}
                      {!u.customPermissions && (
                        <span className="text-sm text-ink-subtle text-ink-muted">
                          Por defecto
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {filteredUsers.length === 0 && (
            <div className="text-center py-12">
              <p className="text-ink-subtle">
                {searchTerm ? 'No se encontraron usuarios' : 'No hay usuarios en esta franquicia'}
              </p>
            </div>
          )}
        </div>

        {/* Legend */}
        <div className="mt-6 p-4 bg-info/10 rounded-lg">
          <h3 className="font-semibold text-info mb-2">
            📖 Leyenda de Permisos
          </h3>
          <ul className="space-y-1 text-sm text-info">
            <li><strong>Crear:</strong> Puede crear nuevos posts</li>
            <li><strong>Editar:</strong> Puede editar sus propios posts</li>
            <li><strong>Borrar:</strong> Puede borrar sus propios posts</li>
            <li><strong>Editar Cualquiera:</strong> Puede editar posts de otros usuarios</li>
            <li><strong>Borrar Cualquiera:</strong> Puede borrar posts de otros usuarios</li>
          </ul>
          <p className="mt-3 text-sm text-info">
            💡 <strong>Tip:</strong> Los usuarios con permisos "Por defecto" usan los permisos de su rol.
            Los permisos personalizados sobrescriben los permisos del rol.
          </p>
        </div>
      </div>
    </div>
  );
}
